import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { MigrationStatusV2, NumberReportEntry, PbxFields, SiteStatus } from '../../shared/models/generated/ziroModels';
import { COLOR_PALETTE, MigrationsService } from './migrations.service';
import { ColorPalette } from './migrations-dashboard.service';

export enum SiteProgressBucket {
  COMPLETE = 'COMPLETE',
  NEARLY_COMPLETE = 'NEARLY_COMPLETE',
  IN_PROGRESS = 'IN_PROGRESS',
  NOT_STARTED = 'NOT_STARTED',
  NO_ELIGIBLE_DIRECTORY_NUMBERS = 'NO_ELIGIBLE_DIRECTORY_NUMBERS',
}

export interface SiteProgressBucketDefinition {
  bucket: SiteProgressBucket;
  labelKey: string;
  color: (typeof COLOR_PALETTE)[ColorPalette];
  isDonutSegment: boolean;
}

const SITE_PROGRESS_BUCKETS: SiteProgressBucketDefinition[] = [
  {
    bucket: SiteProgressBucket.COMPLETE,
    labelKey: 'tkey;migrations.site_progress.bucket.complete.label',
    color: MigrationsService.MIGRATION_STATUS_ROW_MAP.get(MigrationStatusV2.MIGRATED).iconColor,
    isDonutSegment: true,
  },
  {
    bucket: SiteProgressBucket.NEARLY_COMPLETE,
    labelKey: 'tkey;migrations.site_progress.bucket.nearly_complete.label',
    color: MigrationsService.MIGRATION_STATUS_ROW_MAP.get(MigrationStatusV2.STAGED).iconColor,
    isDonutSegment: true,
  },
  {
    bucket: SiteProgressBucket.IN_PROGRESS,
    labelKey: 'tkey;migrations.site_progress.bucket.in_progress.label',
    color: MigrationsService.MIGRATION_STATUS_ROW_MAP.get(MigrationStatusV2.READY).iconColor,
    isDonutSegment: true,
  },
  {
    bucket: SiteProgressBucket.NOT_STARTED,
    labelKey: 'tkey;migrations.site_progress.bucket.not_started.label',
    color: MigrationsService.MIGRATION_STATUS_ROW_MAP.get(MigrationStatusV2.NOT_READY).iconColor,
    isDonutSegment: true,
  },
  {
    bucket: SiteProgressBucket.NO_ELIGIBLE_DIRECTORY_NUMBERS,
    labelKey: 'tkey;migrations.site_progress.bucket.no_eligible_directory_numbers.label',
    color: COLOR_PALETTE.slate,
    isDonutSegment: false,
  },
];

const NEARLY_COMPLETE_THRESHOLD_PERCENT: number = 90;

export interface SiteStatusSegment {
  status: MigrationStatusV2;
  label: string;
  color: string;
  count: number;
  percentOfSite: number;
}

export interface SiteProgress {
  siteId: string;
  siteName: string;
  totalDirectoryNumbers: number;
  eligibleDirectoryNumbers: number;
  migratedDirectoryNumbers: number;
  progressPercent: number;
  bucket: SiteProgressBucket;
  segments: SiteStatusSegment[];
}

export interface SiteProgressReport {
  sites: SiteProgress[];
  unassignedDirectoryNumberCount: number;
}

@Injectable()
export class MigrationsSiteProgressService {
  static DONUT_BUCKETS: SiteProgressBucketDefinition[] = SITE_PROGRESS_BUCKETS.filter(
    (definition: SiteProgressBucketDefinition): boolean => definition.isDonutSegment,
  );

  private static _BUCKET_DEFINITIONS_BY_BUCKET: Map<SiteProgressBucket, SiteProgressBucketDefinition> = new Map(
    SITE_PROGRESS_BUCKETS.map(
      (definition: SiteProgressBucketDefinition): [SiteProgressBucket, SiteProgressBucketDefinition] => [
        definition.bucket,
        definition,
      ],
    ),
  );

  private _translateService: TranslateService = inject(TranslateService);

  static bucketFor(migratedDirectoryNumbers: number, eligibleDirectoryNumbers: number): SiteProgressBucket {
    if (eligibleDirectoryNumbers <= 0) {
      return SiteProgressBucket.NO_ELIGIBLE_DIRECTORY_NUMBERS;
    }

    const percent: number = (migratedDirectoryNumbers / eligibleDirectoryNumbers) * 100;

    if (percent >= 100) {
      return SiteProgressBucket.COMPLETE;
    }
    if (percent >= NEARLY_COMPLETE_THRESHOLD_PERCENT) {
      return SiteProgressBucket.NEARLY_COMPLETE;
    }
    if (percent > 0) {
      return SiteProgressBucket.IN_PROGRESS;
    }
    return SiteProgressBucket.NOT_STARTED;
  }

  private static _definitionFor(bucket: SiteProgressBucket): SiteProgressBucketDefinition {
    return MigrationsSiteProgressService._BUCKET_DEFINITIONS_BY_BUCKET.get(bucket);
  }

  static colorFor(bucket: SiteProgressBucket): string {
    return MigrationsSiteProgressService._definitionFor(bucket).color;
  }

  labelFor(bucket: SiteProgressBucket): string {
    return this._translateService.instant(MigrationsSiteProgressService._definitionFor(bucket).labelKey);
  }

  bucketFromLabel(label: string): SiteProgressBucket {
    if (!label) {
      return null;
    }

    const definition: SiteProgressBucketDefinition = SITE_PROGRESS_BUCKETS.find(
      (candidate: SiteProgressBucketDefinition): boolean =>
        this._translateService.instant(candidate.labelKey).toLowerCase() === label.toLowerCase(),
    );

    return definition ? definition.bucket : null;
  }

  aggregate<PbxFieldsType extends PbxFields>(
    entries: NumberReportEntry<PbxFieldsType>[],
    siteNamesBySiteId: Map<string, string>,
  ): SiteProgressReport {
    const entriesBySiteId: Map<string, NumberReportEntry<PbxFieldsType>[]> = new Map();
    let unassignedDirectoryNumberCount: number = 0;

    siteNamesBySiteId.forEach((siteName: string, siteId: string): void => {
      entriesBySiteId.set(siteId, []);
    });

    entries.forEach((entry: NumberReportEntry<PbxFieldsType>): void => {
      if (!entry.site || entry.site.siteStatus !== SiteStatus.RESOLVED || !entry.site.siteId) {
        unassignedDirectoryNumberCount++;
        return;
      }
      if (!entriesBySiteId.has(entry.site.siteId)) {
        entriesBySiteId.set(entry.site.siteId, []);
      }
      entriesBySiteId.get(entry.site.siteId).push(entry);
    });

    const sites: SiteProgress[] = Array.from(entriesBySiteId.entries())
      .map(([siteId, siteEntries]: [string, NumberReportEntry<PbxFieldsType>[]]): SiteProgress => {
        const siteName: string = siteNamesBySiteId.has(siteId) ? siteNamesBySiteId.get(siteId) : siteId;
        return this._buildSiteProgress(siteId, siteName, siteEntries);
      })
      .sort((first: SiteProgress, second: SiteProgress): number => first.siteName.localeCompare(second.siteName));

    return { sites, unassignedDirectoryNumberCount };
  }

  private _buildSiteProgress<PbxFieldsType extends PbxFields>(
    siteId: string,
    siteName: string,
    entries: NumberReportEntry<PbxFieldsType>[],
  ): SiteProgress {
    const countsByStatus: Map<MigrationStatusV2, number> = new Map();
    Object.values(MigrationStatusV2).forEach((status: MigrationStatusV2): void => {
      countsByStatus.set(status, 0);
    });
    entries.forEach((entry: NumberReportEntry<PbxFieldsType>): void => {
      countsByStatus.set(entry.migrationStatus, countsByStatus.get(entry.migrationStatus) + 1);
    });

    const totalDirectoryNumbers: number = entries.length;
    const ignoredDirectoryNumbers: number = countsByStatus.get(MigrationStatusV2.IGNORED);
    const migratedDirectoryNumbers: number = countsByStatus.get(MigrationStatusV2.MIGRATED);
    const eligibleDirectoryNumbers: number = totalDirectoryNumbers - ignoredDirectoryNumbers;

    return {
      siteId,
      siteName,
      totalDirectoryNumbers,
      eligibleDirectoryNumbers,
      migratedDirectoryNumbers,
      progressPercent: eligibleDirectoryNumbers > 0 ? (migratedDirectoryNumbers / eligibleDirectoryNumbers) * 100 : 0,
      bucket: MigrationsSiteProgressService.bucketFor(migratedDirectoryNumbers, eligibleDirectoryNumbers),
      segments: this._buildSegments(countsByStatus, totalDirectoryNumbers),
    };
  }

  private _buildSegments(
    countsByStatus: Map<MigrationStatusV2, number>,
    totalDirectoryNumbers: number,
  ): SiteStatusSegment[] {
    return Array.from(MigrationsService.MIGRATION_STATUS_ROW_MAP.entries())
      .map(([status, statusRow]): SiteStatusSegment => {
        const count: number = countsByStatus.get(status);
        return {
          status,
          label: statusRow.label,
          color: statusRow.iconColor,
          count,
          percentOfSite: totalDirectoryNumbers > 0 ? (count / totalDirectoryNumbers) * 100 : 0,
        };
      })
      .filter((segment: SiteStatusSegment): boolean => segment.count > 0);
  }
}
